import { I18Head, PageContext } from "@bond-london/gatsby-plugin-cms-i18next";
import classNames from "classnames";
import { HeadFC, PageProps, graphql } from "gatsby";
import React from "react";
import { getImage } from "../components/Blocks";
import { PageContent } from "../containers/PageContent";
import { lookupColourString } from "../lookups";
import { BondSEO, ISite } from "../shared/BondSEO";

const Page: React.FC<PageProps<Queries.SinglePageQuery, PageContext>> = (
  props
) => {
  const {
    data: { graphCmsPage },
    pageContext,
  } = props;
  if (!graphCmsPage) {
    console.error(`No page for ${pageContext.id}`);
    return null;
  }

  return <PageContent page={graphCmsPage} corePath={pageContext.corePath} />;
};

export default Page;

// eslint-disable-next-line import/no-unused-modules
export const Head: HeadFC<Queries.SinglePageQuery, PageContext> = (props) => {
  const { data } = props;
  const site = data.site as ISite;
  const graphCmsPage = data.graphCmsPage as Queries.PageFragment;
  const { title, description, socialImage } = graphCmsPage;
  const pageTitle = `${title} | ${site.siteMetadata.siteName ?? ""}`;

  const videoBlocks = graphCmsPage.blocks.filter(
    (block) =>
      block.__typename === "GraphCMS_ContentBlock" &&
      block.contentBlockType === "DynamicHero" &&
      ((block.image?.mimeType === "video/mp4" &&
        block.image?.localFile?.publicURL) ||
      (block.animation?.mimeType === "video/mp4" &&
        block.animation?.localFile?.publicURL))
  ) as Queries.ContentBlockFragment[];

  const preloadImages = videoBlocks.length > 0 
    ? videoBlocks.length > 1 
      ? ["/image/vid_cover_1.webp", "/image/vid_cover_2.webp"]
      : ["/image/vid_cover_1.webp"]
    : undefined;

  return (
    <>
      <I18Head pageContext={props.pageContext} />
      <BondSEO
        pageTitle={pageTitle}
        site={site}
        pagePath={props.location.pathname}
        pageMetadata={{
          title,
          description,
          image: getImage(socialImage),
          ...(preloadImages && { preloadImages }),
        }}
      />
      <body
        className={classNames(
          // eslint-disable-next-line node/no-process-env
          process.env.GATSBY_DEBUG_TAILWIND && "debug-screens",
          lookupColourString(props.data.graphCmsPage?.backgroundColour, "bg")
        )}
      />
    </>
  );
};

// eslint-disable-next-line import/no-unused-modules
export const PageFragment = graphql`
  fragment Page on GraphCMS_Page {
    id
    title
    linkTitle
    slug
    author
    pageCategory
    date(formatString: "MMMM D YYYY")
    hidden
    noHero
    showTitle
    heroImage {
      ...FullWidthImage
    }
    heroVideo {
      ...Video
    }
    heroStatement {
      ...Statement
    }
    pageLogo {
      ...Svg
    }
    externalHeroVideo
    blocks {
      __typename
      ...ImageBlock
      ...PageLinkFull
      ...ContentBlock
      ...Collection
      ...FormComponent
    }
    fullWidthPageLinks
    backgroundColour
    description
    socialImage {
      ...SeoImageAsset
    }
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const pageQuery = graphql`
  query SinglePage($id: String!) {
    graphCmsPage(id: { eq: $id }) {
      ...Page
    }
    site {
      buildYear: buildTime(formatString: "YYYY")
      buildTime(formatString: "dddd, MMMM DD YYYY, HH:mm:ss")
      siteMetadata {
        siteUrl
        siteName
        logo
        cookieName
      }
    }
  }
`;
